import { useEffect, useState, type FormEvent } from "react";
import { PageHeader, Panel } from "../components/Surface";
import { Button } from "../components/ui/Button";
import { inputClass } from "../components/FormKit";
import { labelize } from "../lib/format";
import {
  listContacts, createContact, updateContact, getContact, listTasks, addTask, setTaskDone,
  CRM_STATUSES, CRM_TYPES, type Contact, type CrmTask,
} from "../lib/crm";

export function CRM() {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [tasks, setTasks] = useState<CrmTask[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", contact_type: "driver_lead", source: "" });
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const [detail, setDetail] = useState<{ contact: Contact; tasks: CrmTask[] } | null>(null);
  const [noteDraft, setNoteDraft] = useState("");
  const [taskDraft, setTaskDraft] = useState("");

  async function refresh() {
    setContacts((await listContacts()).contacts);
    setTasks((await listTasks(true)).tasks);
  }
  useEffect(() => { void refresh(); }, []);

  async function create(e: FormEvent) {
    e.preventDefault();
    if (!form.name.trim()) return;
    setBusy(true);
    try {
      await createContact(form);
      setForm({ name: "", email: "", phone: "", contact_type: "driver_lead", source: "" });
      setShowForm(false);
      await refresh();
    } finally { setBusy(false); }
  }

  async function openContact(id: string) {
    if (open === id) { setOpen(null); setDetail(null); return; }
    setOpen(id);
    const d = await getContact(id);
    setDetail(d);
    setNoteDraft(d.contact.notes ?? "");
  }

  async function move(id: string, status: string) {
    await updateContact({ id, status });
    await refresh();
    if (open === id) setDetail(await getContact(id));
  }

  async function saveNote(id: string) {
    setBusy(true);
    try { await updateContact({ id, notes: noteDraft }); setDetail(await getContact(id)); } finally { setBusy(false); }
  }

  async function newTask(e: FormEvent, contactId: string) {
    e.preventDefault();
    if (!taskDraft.trim()) return;
    setBusy(true);
    try {
      await addTask({ contact_id: contactId, title: taskDraft });
      setTaskDraft("");
      setDetail(await getContact(contactId));
      await refresh();
    } finally { setBusy(false); }
  }

  async function complete(t: CrmTask) {
    await setTaskDone(t.id, true);
    await refresh();
    if (open) setDetail(await getContact(open));
  }

  return (
    <div className="mx-auto grid w-full max-w-4xl gap-6">
      <PageHeader title="CRM" subtitle="Track leads and partners through your pipeline." actions={<Button onClick={() => setShowForm((v) => !v)}>{showForm ? "Cancel" : "Add contact"}</Button>} />

      {tasks.length > 0 && (
        <Panel title={`Follow-ups (${tasks.length})`}>
          <ul className="grid gap-1">
            {tasks.map((t) => (
              <li key={t.id} className="flex items-center justify-between gap-2 text-sm">
                <span><span className="font-bold">{t.title}</span>{t.contact_name ? <span className="text-muted"> · {t.contact_name}</span> : null}{t.due_date ? <span className="font-mono text-[11px] text-muted"> · due {t.due_date}</span> : null}</span>
                <Button variant="ghost" onClick={() => complete(t)}>Done</Button>
              </li>
            ))}
          </ul>
        </Panel>
      )}

      {showForm && (
        <Panel title="New contact">
          <form className="grid gap-3 sm:grid-cols-2" onSubmit={create}>
            <input className={`${inputClass} sm:col-span-2`} placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            <input className={inputClass} placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <input className={inputClass} placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            <select className={inputClass} value={form.contact_type} onChange={(e) => setForm({ ...form, contact_type: e.target.value })} aria-label="Type">
              {CRM_TYPES.map((t) => <option key={t} value={t}>{labelize(t)}</option>)}
            </select>
            <input className={inputClass} placeholder="Source" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} />
            <Button type="submit" disabled={busy || !form.name.trim()} className="sm:col-span-2">Add contact</Button>
          </form>
        </Panel>
      )}

      <div className="grid gap-4 lg:grid-cols-5">
        {CRM_STATUSES.map((stage) => (
          <div key={stage} className="grid content-start gap-2">
            <h2 className="font-mono text-[11px] font-bold uppercase tracking-widest text-muted">{labelize(stage)} ({contacts.filter((c) => c.status === stage).length})</h2>
            {contacts.filter((c) => c.status === stage).map((c) => (
              <button key={c.id} type="button"
                className={`grid gap-1 rounded-card border bg-surface p-3 text-left transition-colors hover:border-accent ${open === c.id ? "border-accent" : "border-line"}`}
                onClick={() => openContact(c.id)}>
                <span className="font-bold">{c.name}</span>
                <span className="font-mono text-[10px] text-muted">{labelize(c.contact_type)}{(c.open_tasks ?? 0) > 0 ? ` · ${c.open_tasks} task` : ""}</span>
              </button>
            ))}
          </div>
        ))}
      </div>

      {open && detail && (
        <Panel title={detail.contact.name} action={<Button variant="ghost" onClick={() => { setOpen(null); setDetail(null); }}>Close</Button>}>
          {detail.contact.email && <p className="font-mono text-[11px] text-muted">{detail.contact.email}{detail.contact.phone ? ` · ${detail.contact.phone}` : ""}</p>}
          <div className="flex flex-wrap gap-1">
            <span className="font-mono text-[11px] uppercase tracking-widest text-muted">Move to:</span>
            {CRM_STATUSES.filter((s) => s !== detail.contact.status).map((s) => (
              <button key={s} type="button" className="rounded border border-line px-2 py-0.5 font-mono text-[11px] text-muted hover:border-accent hover:text-accent-strong" onClick={() => move(detail.contact.id, s)}>→ {labelize(s)}</button>
            ))}
          </div>
          <textarea className={`${inputClass} text-sm`} rows={2} value={noteDraft} onChange={(e) => setNoteDraft(e.target.value)} placeholder="Notes…" onBlur={() => saveNote(detail.contact.id)} />
          <div className="grid gap-1 border-t border-line pt-2">
            {detail.tasks.map((t) => (
              <label key={t.id} className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={t.done} onChange={() => complete(t)} disabled={t.done} />
                <span className={t.done ? "text-muted line-through" : ""}>{t.title}</span>
              </label>
            ))}
            <form className="flex gap-1" onSubmit={(e) => newTask(e, detail.contact.id)}>
              <input className={`${inputClass} text-sm`} value={taskDraft} onChange={(e) => setTaskDraft(e.target.value)} placeholder="Add task…" />
              <Button type="submit" disabled={busy || !taskDraft.trim()}>+</Button>
            </form>
          </div>
        </Panel>
      )}
    </div>
  );
}
